import { useEffect, useId, useRef, useState } from "react";
import { MdCheck, MdClose, MdKeyboardArrowDown } from "react-icons/md";
import Avatar from "components/avatar";
import InputField from "components/fields/InputField";
import { getControlClassName } from "components/fields/FormField";
import { EmptyState } from "components/states";

const HouseholdMultiSelect = ({
  currentUser,
  users,
  selectedMembers,
  setSelectedMembers,
  "aria-labelledby": labelledBy,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const listId = useId();

  const householdOptions = (users || [])
    .filter((u) => u.id !== currentUser?.id)
    .map((user) => ({
      id: user?.id,
      name: user?.firstName + " " + user?.lastName,
      role: user?.role,
      avatar: { url: user?.avatar?.url },
      email: user?.email,
      phone: user?.phone,
      isPrimary: user?.isPrimary || false,
      userDetails: {
        relationshipText: user?.relationshipText || "",
        schoolGrade: user?.schoolGrade || "",
        allergies: user?.allergies || [],
      },
    }));

  const isSelected = (id) => selectedMembers.some((m) => m.id === id);

  const handleSelectMember = (member) => {
    if (!isSelected(member.id)) {
      setSelectedMembers([...selectedMembers, member]);
    } else {
      setSelectedMembers(selectedMembers.filter((m) => m.id !== member.id));
    }
  };

  const handleRemoveMember = (id) => {
    setSelectedMembers(selectedMembers.filter((m) => m.id !== id));
  };

  const filteredMembers = householdOptions.filter((member) =>
    member.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const chosen = selectedMembers?.filter((m) => m.id !== currentUser?.id) || [];

  useEffect(() => {
    if (!dropdownOpen) return undefined;
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [dropdownOpen]);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {chosen.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-2">
          {chosen.map((member) => (
            <li
              key={member.id}
              className="flex items-center gap-1.5 rounded-full bg-surface-subtle py-1 pl-1 pr-2 text-sm text-fg"
            >
              <Avatar size="xs" user={member} name={member.name} noBorder />
              <span>{member.name}</span>
              <button
                type="button"
                onClick={() => handleRemoveMember(member.id)}
                aria-label={`Remove ${member.name}`}
                className="rounded-full text-fg-muted transition-colors hover:text-fg focus-ring"
              >
                <MdClose aria-hidden="true" className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={dropdownOpen}
        aria-controls={dropdownOpen ? listId : undefined}
        aria-labelledby={labelledBy}
        onClick={() => setDropdownOpen((open) => !open)}
        className={getControlClassName({
          className: "flex cursor-pointer items-center justify-between gap-2 text-left",
        })}
      >
        <span className="text-fg-muted">
          {chosen.length > 0 ? `${chosen.length} selected` : "Select people"}
        </span>
        <MdKeyboardArrowDown aria-hidden="true" className="h-5 w-5 text-fg-muted" />
      </button>

      {dropdownOpen && (
        <div
          id={listId}
          className="absolute z-30 mt-2 w-full overflow-hidden rounded-card border border-line bg-surface shadow-overlay"
        >
          <div className="border-b border-line p-2">
            <InputField
              type="search"
              aria-label="Search people"
              placeholder="Search people"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>
          {filteredMembers.length > 0 ? (
            <ul className="max-h-60 overflow-y-auto p-1">
              {filteredMembers.map((member) => {
                const selected = isSelected(member.id);
                return (
                  <li key={member.id}>
                    <button
                      type="button"
                      aria-pressed={selected}
                      onClick={() => handleSelectMember(member)}
                      className="flex w-full items-center justify-between gap-3 rounded-control px-3 py-2 text-left transition-colors hover:bg-surface-subtle focus-ring"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <Avatar size="sm" user={member} name={member.name} noBorder />
                        <span className="min-w-0">
                          <span className="block truncate text-sm text-fg">{member.name}</span>
                          <span className="block text-xs capitalize text-fg-muted">
                            {member.role}
                          </span>
                        </span>
                      </span>
                      {selected && (
                        <MdCheck aria-hidden="true" className="h-5 w-5 shrink-0 text-primary" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <EmptyState variant="inline" title="No people found" />
          )}
        </div>
      )}
    </div>
  );
};

export default HouseholdMultiSelect;
